import { useState, useEffect } from "react";
import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getRemoteConfig,
  fetchAndActivate,
  getValue,
} from "firebase/remote-config";

// Polling interval in milliseconds (default: 5 minutes)
const REMOTE_CONFIG_POLLING_INTERVAL = 5 * 60 * 1000;

const useFirebase = () => {
  const [remoteConfig, setRemoteConfig] = useState(null);
  const [authorizedOrganisations, setAuthorizedOrganisations] = useState([]);
  const [
    isAuthorizedOrganisationsLoading,
    setIsAuthorizedOrganisationsLoading,
  ] = useState(true);

  useEffect(() => {
    console.log("Setting up Firebase...");
    const firebaseConfig = {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY?.toString(),
      authDomain: process.env.REACT_APP_GOOGLE_AUTH_DOMAIN?.toString(),
      projectId: process.env.REACT_APP_PROJECT_ID?.toString(),
      storageBucket: process.env.REACT_APP_STORAGE_BUCKET?.toString(),
      messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID?.toString(),
      appId: process.env.REACT_APP_GOOGLE_APP_ID?.toString(),
    };

    Object.entries(firebaseConfig).forEach(([key, value]) => {
      if (!value) {
        console.warn(
          `Firebase Config Warning: ${key} is missing in .env file!`
        );
      }
    });

    const firebaseApp = getApps()?.length
      ? getApp()
      : initializeApp(firebaseConfig);

    // Safely initialize Remote Config
    try {
      const config = getRemoteConfig(firebaseApp);
      config.settings = {
        minimumFetchIntervalMillis: 0, // Set to 0 to always fetch fresh data
        fetchTimeoutMillis: 60000,
      };

      config.defaultConfig = {
        organisations: JSON.stringify({ organisations: [] }),
      };
      setRemoteConfig(config);
    } catch (err) {
      console.error("Failed to initialize Remote Config:", err);
    }
  }, []);

  // Fetch Remote Config for authorized_organisations when remoteConfig is ready
  // Also set up polling to fetch fresh data periodically
  useEffect(() => {
    const fetchAuthorizedOrganisations = async (isInitialFetch = false) => {
      if (!remoteConfig) {
        setIsAuthorizedOrganisationsLoading(true);
        return;
      }

      if (isInitialFetch) {
        setIsAuthorizedOrganisationsLoading(true);
      }

      try {
        const activated = await fetchAndActivate(remoteConfig);
        console.log(
          "Remote Config fetch completed. config activated:",
          activated
        );

        const authorizedOrgsValue = getValue(remoteConfig, "organisations");
        const authorizedOrgsString = authorizedOrgsValue.asString();
        console.log("Raw organizations value:", authorizedOrgsString);

        if (authorizedOrgsString) {
          const parsedData = JSON.parse(authorizedOrgsString);
          setAuthorizedOrganisations(parsedData?.organisations || []);
          console.log(
            "Authorized organisations fetched:",
            parsedData?.organisations
          );
        } else {
          console.log("No organizations data found, using empty array");
          setAuthorizedOrganisations([]);
        }
      } catch (error) {
        console.error("Failed to fetch authorized organisations:", error);
        setAuthorizedOrganisations([]);
      } finally {
        if (isInitialFetch) {
          setIsAuthorizedOrganisationsLoading(false);
        }
      }
    };

    // Fetch immediately when remoteConfig is available
    fetchAuthorizedOrganisations(true);

    // Set up polling interval to fetch fresh data periodically
    const pollingInterval = setInterval(() => {
      console.log("Polling: Fetching fresh Remote Config data...");
      fetchAuthorizedOrganisations(false);
    }, REMOTE_CONFIG_POLLING_INTERVAL);

    // Cleanup interval on unmount or when remoteConfig changes
    return () => {
      console.log("Cleaning up Remote Config polling interval");
      clearInterval(pollingInterval);
    };
  }, [remoteConfig]);

  return {
    authorizedOrganisations,
    isAuthorizedOrganisationsLoading,
  };
};

export default useFirebase;
